import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { packListsApi, marketsApi } from '../../services/api';
import AdminPageHeader from '../../components/AdminPageHeader';
import './PackLists.css';

export default function PackLists() {
  const [packLists, setPackLists] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [pickerType, setPickerType] = useState(null); // 'CC' | 'AR_SAT' | 'AR_SUN' | null
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [plData, mData] = await Promise.all([
        packListsApi.getAll(),
        marketsApi.getAll(),
      ]);
      const sorted = (plData.pack_lists || []).sort((a, b) => new Date(b.week_date) - new Date(a.week_date));
      setPackLists(sorted);
      setMarkets((mData.markets || []).filter(m => m.is_active));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function openPicker(type) {
    setPickerType(type);
  }

  function closePicker() {
    setPickerType(null);
  }

  async function createForMarket(market) {
    const type = pickerType;
    setCreating(true);
    setError('');
    try {
      const weekDate = getUpcomingDate(type === 'AR_SUN' ? 0 : type === 'AR_SAT' ? 6 : 0);
      const result = await packListsApi.create({ type, week_date: weekDate, market_id: market.id, market_name: market.name });
      setPickerType(null);
      navigate(`/ops/pack-lists/${result.id}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  }

  const COOLER_CATS = ['ar_refrigerated', 'cc_veg', 'cc_brine', 'cc_bev', 'cc_keg'];
  const FREEZER_CATS = ['ar_frozen', 'ar_chicken', 'ar_pork'];

  async function handlePrintMaster(type) {
    setPrinting(true);
    try {
      // Find most recent pack lists of this type
      const typeLists = packLists.filter(pl => pl.type === type);
      alert('Found ' + typeLists.length + ' lists. Total pack lists: ' + packLists.length + '. Types: ' + [...new Set(packLists.map(pl => pl.type))].join(', '));
      if (typeLists.length === 0) { alert('No ' + type + ' pack lists found.'); setPrinting(false); return; }

      // Group by week_date and get the most recent week
      const dates = [...new Set(typeLists.map(pl => String(pl.week_date).slice(0,10)))].sort().reverse();
      const mostRecentDate = dates[0];
      const listsForWeek = typeLists.filter(pl => String(pl.week_date).slice(0,10) === mostRecentDate);

      if (type === 'AR_SUN') {
        // Sunday: individual pages per market
        const allItems = await Promise.all(listsForWeek.map(async pl => {
          const res = await packListsApi.getItems(pl.id);
          return { market: pl.market_name, items: res.items || [] };
        }));

        const pages = allItems.map(({ market, items }) => `
          <div style="page-break-after: always;">
            <h2>${market} — Sunday Pack List</h2>
            <p>Week of ${mostRecentDate}</p>
            <table border="1" cellpadding="6" cellspacing="0" width="100%" style="border-collapse:collapse;font-size:11px">
              <thead><tr style="background:#2c3e50;color:white"><th>Product</th><th>Qty</th><th>Notes</th></tr></thead>
              <tbody>${items.map(i => `<tr><td>${i.product_name||''}</td><td style="text-align:center">${i.qty_confirmed||i.qty_suggested||''}</td><td style="min-width:80px"></td></tr>`).join('')}</tbody>
            </table>
          </div>`).join('');

        const html = `<html><head><title>Sunday Master Pack List - ${mostRecentDate}</title>
          <style>body{font-family:Arial,sans-serif;font-size:11px;margin:20px} h2{margin-bottom:4px}</style>
          </head><body>${pages}</body></html>`;
        const win = window.open('', '_blank');
        if (!win) { alert('Please allow popups.'); setPrinting(false); return; }
        win.document.write(html);
        win.document.close();
        setTimeout(() => win.print(), 500);

      } else {
        // Saturday: wide layout, all markets side by side
        const allData = await Promise.all(listsForWeek.map(async pl => {
          const res = await packListsApi.getItems(pl.id);
          return { market: pl.market_name, items: res.items || [] };
        }));

        // Sort markets alphabetically
        allData.sort((a, b) => a.market.localeCompare(b.market));

        // Get all unique products across all markets
        const allProducts = {};
        allData.forEach(({ items }) => {
          items.forEach(item => {
            if (!allProducts[item.product_id]) {
              allProducts[item.product_id] = {
                id: item.product_id,
                name: item.product_name,
                category: item.category,
                company: item.company
              };
            }
          });
        });

        const COOLER_ORDER = ['ar_refrigerated', 'cc_veg', 'cc_brine', 'cc_bev', 'cc_keg'];
        const FREEZER_ORDER = [
          { key: 'ar_frozen', label: 'Value Added & Prepared' },
          { key: 'ar_chicken', label: 'Chicken' },
          { key: 'ar_pork', label: 'Pork' },
        ];
        const COOLER_LABELS = {
          'ar_refrigerated': 'Refrigerated',
          'cc_veg': 'Fermented Veg',
          'cc_brine': 'Brines',
          'cc_bev': 'Kombucha & Beverages',
          'cc_keg': 'Kegs',
        };

        const coolerProducts = Object.values(allProducts).filter(p => COOLER_CATS.includes(p.category));
        const freezerProducts = Object.values(allProducts).filter(p => FREEZER_CATS.includes(p.category));

        const marketHeaders = allData.map(d => `<th style="text-align:center">${d.market}</th>`).join('');
        const subHeaders = '';

        const productRow = (product) => {
          const cells = allData.map(({ items }) => {
            const item = items.find(i => String(i.product_id) === String(product.id));
            return `<td style="text-align:center">${item?.qty_suggested || ''}</td>`;
          }).join('');
          return `<tr><td>${product.name}</td>${cells}</tr>`;
        };

        const sectionHeader = (label) => `<tr><td colspan="${1 + allData.length}" style="background:#2c3e50;color:white;font-weight:bold;padding:6px 8px;font-size:12px">${label}</td></tr>`;

        const html = `<html><head><title>Saturday Master Pack List - ${mostRecentDate}</title>
          <style>
            body{font-family:Arial,sans-serif;font-size:10px;margin:10px}
            h2{margin-bottom:4px}
            table{border-collapse:collapse;width:100%}
            th,td{border:1px solid #ccc;padding:4px 6px}
            th{background:#f0f0f0;font-weight:bold}
          </style></head>
          <body>
            <h2>Saturday Master Pack List — Week of ${mostRecentDate}</h2>
            <p>Printed: ${new Date().toLocaleDateString()}</p>
            <table>
              <thead>
                <tr><th>Product</th>${marketHeaders}</tr>
              </thead>
              <tbody>
                ${sectionHeader('🧊 COOLER ITEMS')}
                ${COOLER_ORDER.map(cat => {
                  const prods = coolerProducts.filter(p => p.category === cat);
                  if (prods.length === 0) return '';
                  return `<tr><td colspan="${1 + allData.length}" style="background:#d4e6d4;font-weight:bold;padding:4px 8px;font-size:10px">${COOLER_LABELS[cat] || cat}</td></tr>` +
                    prods.map(productRow).join('');
                }).join('')}
                ${sectionHeader('❄️ FREEZER ITEMS')}
                ${FREEZER_ORDER.map(({ key, label }) => {
                  const prods = freezerProducts.filter(p => p.category === key);
                  if (prods.length === 0) return '';
                  return `<tr><td colspan="${1 + allData.length}" style="background:#d4e6d4;font-weight:bold;padding:4px 8px;font-size:10px">${label}</td></tr>` +
                    prods.map(productRow).join('');
                }).join('')}
              </tbody>
            </table>
          </body></html>`;

        const win = window.open('', '_blank');
        if (!win) { alert('Please allow popups.'); setPrinting(false); return; }
        win.document.write(html);
        win.document.close();
        setTimeout(() => win.print(), 500);
      }
    } catch (e) {
      alert('Error: ' + e.message);
    } finally {
      setPrinting(false);
    }
  }

  function getUpcomingDate(dayOfWeek) {
    const today = new Date();
    const day = today.getDay();
    const diff = (dayOfWeek - day + 7) % 7 || 7;
    const next = new Date(today);
    next.setDate(today.getDate() + diff);
    return next.toISOString().split('T')[0];
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function typeBadge(type) {
    return <span className={`company-badge ${type === 'CC' ? 'badge-cc' : 'badge-ar'}`}>{type === 'AR_SAT' ? 'AR Sat' : type === 'AR_SUN' ? 'AR Sun' : type}</span>;
  }

  const pickerMarkets = pickerType === 'AR_SAT' ? markets.filter(m => m.day_of_week === 'Saturday')
    : pickerType === 'AR_SUN' ? markets.filter(m => m.day_of_week === 'Sunday')
    : markets;

  const pickerLabel = pickerType === 'CC' ? 'CC Pack List' : pickerType === 'AR_SAT' ? 'AR Saturday Pack List' : 'AR Sunday Pack List';

  return (
    <div className="page">
      <AdminPageHeader
        title="Pack Lists"
        subtitle={`${packLists.length} pack lists`}
        action={
          <div className="header-actions">
            <button className="btn-secondary" onClick={() => openPicker('CC')} disabled={creating}>
              + New CC Pack List
            </button>
            <button className="btn-primary" onClick={() => openPicker('AR_SAT')} disabled={creating}>
              + New AR Saturday
            </button>
            <button className="btn-secondary" onClick={() => openPicker('AR_SUN')} disabled={creating}>
              + New AR Sunday
            </button>
            <button className="btn-secondary" onClick={() => handlePrintMaster('AR_SAT')} disabled={printing}>
              {printing ? 'Loading...' : '🖨 Print Saturday Master'}
            </button>
            <button className="btn-secondary" onClick={() => handlePrintMaster('AR_SUN')} disabled={printing}>
              {printing ? 'Loading...' : '🖨 Print Sunday Master'}
            </button>
          </div>
        }
      />

      {pickerType && (
        <div className="market-picker-overlay" onClick={closePicker}>
          <div className="market-picker-panel" onClick={e => e.stopPropagation()}>
            <div className="market-picker-header">
              <h3>Select Market for {pickerLabel}</h3>
              <button className="market-picker-close" onClick={closePicker}>✕</button>
            </div>
            <div className="market-picker-grid">
              {pickerMarkets.length === 0 ? (
                <div className="market-picker-empty">No active markets found for this day.</div>
              ) : (
                pickerMarkets.map(market => (
                  <button
                    key={market.id}
                    className="market-picker-btn"
                    onClick={() => createForMarket(market)}
                    disabled={creating}
                  >
                    {market.name}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="page-body">
        {error && <div className="error-banner">{error}</div>}

        {loading ? (
          <div className="loading-state">Loading pack lists...</div>
        ) : packLists.length === 0 ? (
          <div className="empty-state">No pack lists yet. Create your first one above.</div>
        ) : (
          <div className="markets-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Week Of</th>
                  <th>Market</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Created By</th>
                </tr>
              </thead>
              <tbody>
                {packLists.map(pl => (
                  <tr key={pl.id} onClick={() => navigate(`/ops/pack-lists/${pl.id}`)} style={{ cursor: 'pointer' }}>
                    <td>{formatDate(pl.week_date)}</td>
                    <td className="td-name">{pl.market_name || '—'}</td>
                    <td>{typeBadge(pl.type)}</td>
                    <td><span className="pill pill-blue">{pl.status}</span></td>
                    <td>{pl.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
